import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const isCancel = location.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); if (user) await refresh(); return; }
        if (data.payment_status === "processing") { setStatus("processing"); if (user) await refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (_) {}
      if (tries++ < 8) setTimeout(poll, 2000); else setStatus("failed");
    };
    poll();
  }, [sessionId, isCancel, refresh, user]);

  const cfg = {
    checking: { icon: null, title: "Confirming your payment...", desc: "Please wait a moment." },
    paid: { icon: CheckCircle2, cls: "text-secondary", title: "Payment successful!", desc: "Your plan is now active. Thank you." },
    processing: { icon: Clock, cls: "text-primary", title: "Bank payment initiated", desc: "Direct debits take 3–5 business days to clear. We'll mark this fee paid and email your receipt once the bank confirms." },
    cancelled: { icon: XCircle, cls: "text-muted-foreground", title: "Payment cancelled", desc: "No charge was made." },
    failed: { icon: XCircle, cls: "text-destructive", title: "We couldn't confirm your payment", desc: "If you were charged, it will reflect shortly." },
  }[status];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="text-center max-w-md" data-testid="payment-result">
        {status === "checking" ? (
          <div className="h-12 w-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-5" />
        ) : cfg.icon && <cfg.icon size={56} className={`mx-auto mb-5 ${cfg.cls}`} />}
        <h1 className="font-heading font-extrabold text-2xl">{cfg.title}</h1>
        <p className="text-muted-foreground mt-2">{cfg.desc}</p>
        <Button onClick={() => navigate(user ? "/app" : "/")} className="mt-6 brand-gradient text-white border-0 hover:opacity-90" data-testid="payment-continue-btn">
          {user ? "Back to dashboard" : "Back home"}
        </Button>
      </div>
    </div>
  );
}
